import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Cable, GraduationCap, Server, Home, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const Projects = () => {
  const { language, t } = useLanguage();

  const projects = [
    {
      id: 'telecabine',
      icon: <Cable className="h-12 w-12 text-secondary" />,
      title: language === 'fr' ? 'Téléphérique Minier et Agricole' : 'Mining and Agricultural Cable Car',
      description: language === 'fr' 
        ? 'Un système de transport par câble moderne pour faciliter le transport des ressources minières et des produits agricoles à travers les terrains difficiles de la RDC. Ce projet révolutionnaire améliore l\'efficacité logistique et réduit les coûts de transport.'
        : 'A modern cable car transportation system to facilitate the transport of mining resources and agricultural products across difficult terrains in DRC. This revolutionary project improves logistical efficiency and reduces transportation costs.',
      image: 'https://images.unsplash.com/photo-1682373440093-a4f50cbda95e?crop=entropy&cs=srgb&fm=jpg&q=85',
      status: language === 'fr' ? 'En Cours' : 'In Progress',
      progress: 45
    },
    {
      id: 'school',
      icon: <GraduationCap className="h-12 w-12 text-secondary" />,
      title: language === 'fr' ? 'École de Technologie Informatique' : 'Computer Technology Based School',
      description: language === 'fr' 
        ? 'Une institution éducative moderne axée sur les technologies de l\'information, la programmation et les compétences numériques. Formation de la prochaine génération de professionnels de la technologie en RDC avec des programmes internationalement reconnus.'
        : 'A modern educational institution focused on information technology, programming, and digital skills. Training the next generation of technology professionals in DRC with internationally recognized programs.',
      image: 'https://images.unsplash.com/photo-1596496356933-9b6e0b186b88?crop=entropy&cs=srgb&fm=jpg&q=85',
      status: language === 'fr' ? 'En Cours' : 'In Progress',
      progress: 60
    },
    {
      id: 'datacenter',
      icon: <Server className="h-12 w-12 text-secondary" />,
      title: language === 'fr' ? 'Centre de Données' : 'Data Center',
      description: language === 'fr' 
        ? 'Infrastructure de centre de données de classe mondiale pour héberger des services cloud, du stockage sécurisé et des solutions d\'hébergement pour les entreprises en RDC. Infrastructure certifiée Tier III avec redondance complète et sécurité maximale.'
        : 'World-class data center infrastructure to host cloud services, secure storage, and hosting solutions for businesses in DRC. Tier III certified infrastructure with complete redundancy and maximum security.',
      image: 'https://images.unsplash.com/photo-1695668548342-c0c1ad479aee?crop=entropy&cs=srgb&fm=jpg&q=85',
      status: language === 'fr' ? 'En Cours' : 'In Progress',
      progress: 35
    },
    {
      id: 'house-id',
      icon: <Home className="h-12 w-12 text-secondary" />,
      title: language === 'fr' ? 'Identification des Maisons' : 'House Identification',
      description: language === 'fr' 
        ? 'Système numérique d\'identification et de cartographie des propriétés pour améliorer la gestion urbaine, faciliter la livraison des services et renforcer la sécurité. Intégration avec les systèmes GPS et les bases de données gouvernementales.'
        : 'Digital property identification and mapping system to improve urban management, facilitate service delivery, and enhance security. Integration with GPS systems and government databases.',
      image: 'https://images.pexels.com/photos/8090149/pexels-photo-8090149.jpeg',
      status: language === 'fr' ? 'En Cours' : 'In Progress',
      progress: 55
    }
  ];

  return (
    <div className="min-h-screen">
      <section 
        className="relative h-96 flex items-center justify-center overflow-hidden"
        data-testid="projects-hero"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="relative z-10 text-center text-white px-4">
          <motion.h1 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-serif text-4xl sm:text-5xl font-bold mb-4"
          >
            {language === 'fr' ? 'Projets en Cours' : 'Projects in Progress'}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg max-w-2xl mx-auto opacity-90"
          >
            {language === 'fr' 
              ? 'Découvrez nos initiatives innovantes pour transformer la RDC'
              : 'Discover our innovative initiatives to transform DRC'}
          </motion.p>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-background" data-testid="projects-grid">
        <div className="max-w-7xl mx-auto space-y-16">
          {projects.map((project, idx) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: idx * 0.1 }}
              viewport={{ once: true }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${
                idx % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
              data-testid={`project-${project.id}`}
            >
              <div className={`${idx % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="relative h-96 overflow-hidden rounded-lg shadow-xl">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-secondary text-white px-4 py-2 rounded-full text-sm font-semibold">
                    {project.status}
                  </div>
                </div>
              </div>

              <div className={`${idx % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className="mb-6">
                  {project.icon}
                </div>
                <h3 className="font-serif text-3xl font-bold mb-4 text-foreground">
                  {project.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  {project.description}
                </p>

                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-foreground">
                      {language === 'fr' ? 'Progression' : 'Progress'}
                    </span>
                    <span className="text-sm font-bold text-primary">
                      {project.progress}%
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-secondary to-primary transition-all duration-1000"
                      style={{ width: `${project.progress}%` }}
                    ></div>
                  </div>
                </div>

                <button className="inline-flex items-center space-x-2 text-primary font-semibold hover:text-secondary transition-colors">
                  <span>{language === 'fr' ? 'En Savoir Plus' : 'Learn More'}</span>
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-primary text-white" data-testid="projects-cta">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="font-serif text-3xl font-bold mb-6"
          >
            {language === 'fr' ? 'Intéressé par Nos Projets?' : 'Interested in Our Projects?'}
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-lg mb-8 opacity-90"
          >
            {language === 'fr' 
              ? 'Contactez-nous pour découvrir les opportunités de partenariat et d\'investissement dans ces projets transformateurs.'
              : 'Contact us to discover partnership and investment opportunities in these transformative projects.'}
          </motion.p>
          <motion.a
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            href="/contact"
            className="inline-block bg-secondary text-white px-8 py-4 text-lg font-sans font-semibold hover:bg-secondary/90 transition-colors uppercase"
            data-testid="projects-contact-btn"
          >
            {language === 'fr' ? 'Nous Contacter' : 'Contact Us'}
          </motion.a>
        </div>
      </section>
    </div>
  );
};

export default Projects;
